import { DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { gridFilteredSortedRowIdsSelector, useGridApiRef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { AssortmentItemMinimal, AssortmentItemPriorityDTO } from '@retail/my-assortment/types';
import { ContainedButton, Dialog, HelperText, OutlinedButton } from '@shared/components';
import { useCallback, useMemo, useState } from 'react';
import { BulkEditSelectedItems } from './BulkEditSelectedItems';
import { useToast } from '@retail/hooks';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { ItemPriority } from '@retail/products/types';
import { getISO_8601Date } from '@retail/utils';
import { useFilters } from '@retail/products/hooks';
import { BulkActionsFilters, itemBulkActions } from '@retail/products/utils';

interface Props {
  action?: 'edit' | 'create';
  open: boolean;
  selectedItems: AssortmentItemMinimal[];
  addItemsToAssortment: (items: AssortmentItemPriorityDTO[]) => Promise<void>;
  resetCheckedItems: () => void;
  onClose: () => void;
}

function BulkEditSelectedItemsDialog({
  action = 'create',
  selectedItems,
  addItemsToAssortment,
  resetCheckedItems,
  onClose,
}: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const toast = useToast();
  const [duplicateRowIds, setDuplicateRowIds] = useState<number[]>([]);

  const { resetFilters } = useFilters<BulkActionsFilters>(itemBulkActions);
  const handleClose = () => {
    resetFilters();
    onClose();
  };

  const [items, setItems] = useState<AssortmentItemMinimal[]>(selectedItems);
  const [rowCount, setRowCount] = useState<number>(selectedItems.length);

  const toItemDTO = useCallback(
    (item: AssortmentItemMinimal, withStockedStatus: boolean): AssortmentItemPriorityDTO => ({
      gtinCode: item.gtinCode,
      mgItemNumber: item.mgItemNumber,
      validFrom: getISO_8601Date(item.validFrom),
      validTo: item.validTo ? getISO_8601Date(item.validTo) : undefined,
      priority: item.priority?.prioritised ? ItemPriority.TEN : ItemPriority.ZERO,
      isStocked: withStockedStatus ? item.isStocked : undefined,
      mgSupplierId: item.supplier.id,
    }),
    []
  );

  const duplicateItems = useMemo(
    () => items.filter((item) => duplicateRowIds.includes(item.mgItemNumber)),
    [duplicateRowIds, items]
  );

  const removeItem = useCallback(
    (mgSupplierItemId: number) => {
      const updatedItems = apiRef.current
        ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
        : [];

      setItems(updatedItems.filter((item) => item.mgSupplierItemId !== mgSupplierItemId));
    },
    [apiRef]
  );

  const onAddItems = useCallback(() => {
    const updatedItems = apiRef.current
      ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
      : [];

    addItemsToAssortment(updatedItems.map((item) => toItemDTO(item, true)))
      .then(() => {
        setItems([]);

        resetCheckedItems();
        onClose();
      })

      .catch((e) => {
        console.error(e);
        toast.error(t('products.errors.generic'));
      });
  }, [apiRef, addItemsToAssortment, toItemDTO, resetCheckedItems, onClose, toast, t]);

  const onAddFilteredItems = useCallback(() => {
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef);

    const updatedItems = apiRef.current
      ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
      : [];

    const filteredItems = updatedItems.filter((item) => rowIds.includes(item.mgSupplierItemId));
    const restItems = updatedItems.filter((item) => !rowIds.includes(item.mgSupplierItemId));

    addItemsToAssortment(filteredItems.map((item) => toItemDTO(item, false)))
      .then(() => {
        setItems(restItems);

        if (restItems.length === 0) {
          resetCheckedItems();
          onClose();
        }
      })

      .catch((e) => {
        console.error(e);
        toast.error(t('products.errors.generic'));
      });
  }, [apiRef, addItemsToAssortment, toItemDTO, resetCheckedItems, onClose, toast, t]);

  return (
    <Dialog fullWidth maxWidth="xl" open onClose={handleClose}>
      <DialogTitle>{t('products.mgItem.addItemsToAssortment.handleSelectedItems')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexGrow: 1, height: '100vh' }} className="padding-0">
        <BulkEditSelectedItems
          apiRef={apiRef}
          selectedItems={items}
          duplicateRowIds={duplicateRowIds}
          rowCount={rowCount}
          setDuplicateRowIds={setDuplicateRowIds}
          setRowCount={setRowCount}
          removeItem={removeItem}
        />
      </DialogContent>
      <DialogActions>
        <Stack flexDirection="column" alignItems="flex-end">
          {duplicateRowIds.length > 0 && (
            <HelperText id={'duplicatePrio'} status={'error'}>
              {t('products.errors.multipleMainPrio')}
            </HelperText>
          )}
          <Stack flexDirection="row" gap={1}>
            <OutlinedButton size="small" onClick={handleClose}>
              {t('common.cancel')}
            </OutlinedButton>
            {rowCount < items.length && (
              <ContainedButton
                size="small"
                onClick={onAddFilteredItems}
                disabled={
                  gridFilteredSortedRowIdsSelector(apiRef).filter((id) => {
                    const duplicates = duplicateItems.map((item) => item.mgSupplierItemId);
                    return duplicates.includes(Number(id));
                  }).length > 0
                }
              >
                {rowCount === 1
                  ? action === 'create'
                    ? t('products.mgItem.supplierItem.addFilteredItem')
                    : t('products.mgItem.supplierItem.updateFilteredItem')
                  : action === 'create'
                  ? t('products.mgItem.supplierItem.addFilteredItems', { count: rowCount })
                  : t('products.mgItem.supplierItem.updateFilteredItems', { count: rowCount })}
              </ContainedButton>
            )}
            <ContainedButton
              size="small"
              onClick={onAddItems}
              disabled={duplicateRowIds.length > 0}
            >
              {action === 'create'
                ? t('products.mgItem.supplierItem.addAllItems')
                : t('products.mgItem.supplierItem.updateAllItems')}
            </ContainedButton>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(BulkEditSelectedItemsDialog);
